import {
  ActivityQuery,
  ControlCode,
  Finding,
  FindingsActivity,
  getControlName,
  useRecentEndpointQuery,
} from "@theprelude/core";
import { ArrowRight, Button } from "@theprelude/ds";
import { useMemo } from "react";
import { osMapping } from "@/lib/operating-system";
import { useConfigurations } from "./configurations";

const formatFinding = (
  finding: Finding,
  policyNames: Record<string, string> = {},
) => {
  let value = finding.value;
  if (finding.value && finding.key === "dos") {
    value = osMapping[finding.value] ?? "";
  }
  if (finding.value && finding.key === "control") {
    value = getControlName(finding.value as ControlCode);
  }

  if (finding.value && finding.key === "policy") {
    value = policyNames[finding.value] ?? finding.value;
  }

  return {
    key: finding.key,
    value,
    improvement: finding.improvement,
  };
};

const formatImprovement = (
  improvement: FindingsActivity,
  policyNames = {} as Record<string, string>,
): Record<string, string> => {
  const policyName =
    policyNames[improvement.policy ?? ""] ?? improvement.policy ?? "";
  return {
    dos: osMapping[improvement.dos] ?? "",
    control: getControlName(improvement.control),
    policy: policyName,
    os: improvement.os ?? "",
  };
};

const Recommendation: React.FC<{
  setSeeRecommendation: (b: boolean) => void;
  query: ActivityQuery;
}> = ({ setSeeRecommendation, query }) => {
  const { powerQuery } = useConfigurations(query);

  const data = useMemo(() => {
    return powerQuery.data ?? [];
  }, [powerQuery.data]);

  const { data: policyNames } = useRecentEndpointQuery((endpoints) => {
    const policyNames: Record<string, string> = {};
    for (const endpoint of endpoints) {
      if (endpoint.policy) {
        policyNames[endpoint.policy] = endpoint.policy_name ?? endpoint.policy;
      }
    }
    return policyNames;
  });

  const mostImprovement = useMemo(() => {
    return data
      .filter((d) => d.finding)
      .sort((a, b) => {
        if (!a.finding || !b.finding) {
          return 0;
        }
        return a.finding.improvement > b.finding.improvement ? -1 : 1;
      })[0];
  }, [data]);

  if (!mostImprovement) {
    return null;
  }

  const finding = mostImprovement?.finding;

  const formattedMostImproved = formatImprovement(mostImprovement, policyNames);

  return (
    <div className="flex items-center bg-secondary-background p-4">
      <div>
        <p className="m-0">
          We recommend making the following configuration change:
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {finding &&
            Object.keys(formattedMostImproved)
              .filter((k) => k !== "finding")
              .map((key) => (
                <Improvement
                  key={key}
                  focus={key}
                  value={formattedMostImproved[key] ?? ""}
                  finding={formatFinding(finding, policyNames)}
                />
              ))}
          <span className="text-status-green">
            +{finding?.improvement}% improvment
          </span>
        </div>
      </div>
      <Button
        className="ml-auto"
        intent="secondary"
        onClick={() => setSeeRecommendation(false)}
      >
        Ignore
      </Button>
    </div>
  );
};

const Improvement: React.FC<{
  focus: string;
  value: string;
  finding: Finding;
}> = ({ focus, value, finding }) => {
  if (!value) {
    return null;
  }

  return (
    <div className="flex w-max gap-1 rounded-xl border border-stroke-line bg-primary-background px-3 py-1">
      <span>{value}</span>
      {focus === finding.key && (
        <div className="flex items-center gap-1">
          <ArrowRight className="text-secondary-foreground" />
          <span className="text-status-green">{finding.value}</span>
        </div>
      )}
    </div>
  );
};

export default Recommendation;
